import moment from "moment-timezone"
import {
  checkInBookedSession,
  createSessionBooking,
  getCoachBookingAvailabilities,
  getCoachTimeslotsBySessionType,
  getUpcomingSessions,
} from "@/api/student/schedule"
import { BOOKING_STATUS } from "@/constants/schedule"
import {CURRENCY_TYPES} from "@/store/user/inventory/inventory.constants";
import {PAYMENT_METHODS} from "@/store/coaching/coaching.constants";

const state = {
  showScheduleModal: false,
  loading: false,
  coachAvailableTimeSlots: [],
  selectedDate: "",
  coachAvailableTimeSlotsByDate: [],
  coach: null,
  gameKey: "leagueoflegends",
  type: "in-game",
  quantity: 1,
  scheduledSlots: [],
  currentSession: 1,
  contactInfo: {
    region: "",
    platform: "",
    username: "",
    discord: "",
  },
  failedSchedules: [],
  upcomingSessions: [],
  refillingStep: false,
}

const getters = {
  coachAvailableTimeSlots: state => state.coachAvailableTimeSlots,
  selectedDate: state => state.selectedDate,
  coachAvailableTimeSlotsByDate: state => state.coachAvailableTimeSlotsByDate,
  gameKey: state => state.gameKey,
  type: state => state.type,
  quantity: state => state.quantity,
  scheduledSlots: state => state.scheduledSlots,
  currentSession: state => state.currentSession,
  coach: state => state.coach,
  service: state =>
    state.coach.games[state.gameKey].services.find(
      service => service.session_type.key === state.type
    ),
  regions: state => state.coach.games[state.gameKey].regions,
  platforms: state => state.coach.games[state.gameKey].platforms,
  contactInfo: state => state.contactInfo,
  loading: state => state.loading,
  failedSchedules: state => state.failedSchedules,
  allFailedHuh: state =>
    state.failedSchedules.length === state.scheduledSlots.length,
  partialFailedHuh: state =>
    state.failedSchedules.length &&
    state.failedSchedules.length < state.scheduledSlots.length,
  upcomingSessions: state => state.upcomingSessions,
  refillingStep: state => state.refillingStep,
  showScheduleModal: state => state.showScheduleModal,
  // Synced state from general coaching service order flow
  selectedPaymentMethod: (state, getters, rootState, rootGetters) => {
    return rootGetters["Coaching/CoachService/selectedPaymentMethod"]
  },
}

const mutations = {
  setCoachAvailableTimeSlots: (state, coachAvailableTimeSlots) =>
    (state.coachAvailableTimeSlots = coachAvailableTimeSlots),
  setSelectedDate: (state, selectedDate) => (state.selectedDate = selectedDate),
  setCoachAvailableTimeSlotsByDate: (state, coachAvailableTimeSlotsByDate) =>
    (state.coachAvailableTimeSlotsByDate = coachAvailableTimeSlotsByDate),
  setGameKey: (state, gameKey) => (state.gameKey = gameKey),
  setType: (state, type) => (state.type = type),
  setQuantity: (state, quantity) => (state.quantity = quantity),
  setScheduledSlots: (state, slots) => (state.scheduledSlots = [...slots]),
  setCurrentSession: (state, session) => (state.currentSession = session),
  setCoach: (state, coach) => (state.coach = { ...coach }),
  setContactInfo: (state, contactInfo) =>
    (state.contactInfo = { ...contactInfo }),
  setLoading: (state, loading) => (state.loading = loading),
  setFailedSchedules: (state, failedSchedules) =>
    (state.failedSchedules = failedSchedules),
  setUpcomingSessions: (state, sessions) => (state.upcomingSessions = sessions),
  setRefillingStep: (state, refillingStep) =>
    (state.refillingStep = refillingStep),
  setShowScheduleModal: (state, showScheduleModal) =>
    (state.showScheduleModal = showScheduleModal),
}

const actions = {
  resetScheduling ({ commit }) {
    commit("setSelectedDate", "")
    commit("setCoachAvailableTimeSlotsByDate", [])
    commit("setCoachAvailableTimeSlots", [])
    commit("setScheduledSlots", [])
    commit("setCurrentSession", 1)
    commit("setFailedSchedules", [])
  },
  async getCoachBookingAvailabilities (
    { state, commit, dispatch },
    { period, coachId }
  ) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const payload = {}
    if (coachId) {
      payload.coachId = coachId
    } else {
      payload.coachId = state.coach.coachId
    }
    payload.from = moment
      .utc()
      .set({
        year: period.year,
        month: period.month - 1,
        date: 1,
      })
      .add(-2, "day")
      .format("YYYY-MM-DD")
    payload.to = moment
      .utc()
      .set({
        year: period.year,
        month: period.month,
        date: 2,
      })
      .format("YYYY-MM-DD")

    const data = await getCoachBookingAvailabilities(
      instaProAccessToken,
      payload
    )
    if (data.success) {
      commit("setCoachAvailableTimeSlots", data.result)
    }
  },
  async getCoachAvailableTimeSlotsByDate (
    { state, commit, dispatch },
    { date, coachId, gameId }
  ) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const payload = {
      coachId: coachId || state.coach.coachId,
      gameId: gameId || state.coach.games[state.gameKey].id,
      date,
      type: state.type,
    }
    const data = await getCoachTimeslotsBySessionType(
      instaProAccessToken,
      payload
    )
    if (data.success) {
      const slots = data.result.filter(slot => {
        return (
          !slot.booked &&
          moment(payload.date).format("YYYY-MM-DD") ===
            moment(slot.start).format("YYYY-MM-DD")
        )
      })
      commit("setCoachAvailableTimeSlotsByDate", slots)
    }
  },
  backToPrevSession ({ commit, state }) {
    if (state.currentSession <= state.quantity + 1) {
      const scheduledSlots = [...state.scheduledSlots]
      scheduledSlots.pop()
      commit("setScheduledSlots", scheduledSlots)
    }
    commit("setCurrentSession", state.currentSession - 1)
  },
  removeSlot ({ commit, state }, index) {
    const scheduledSlots = [...state.scheduledSlots]
    scheduledSlots.splice(index, 1)
    commit("setScheduledSlots", scheduledSlots)
    commit("setCurrentSession", state.currentSession - 1)
  },
  async bookSessions ({ commit, state, getters, dispatch }, payload) {
    commit("setLoading", true)

    const promises = []

    for (let startTime of state.scheduledSlots) {
      let body
      if (payload) {
        body = payload
        body.start_time = moment.utc(startTime).format()
        body.payment_method = getters.selectedPaymentMethod
      } else {
        body = {
          coach_id: state.coach.id,
          game: state.gameKey,
          game_id: state.coach.games[state.gameKey].id,
          type: state.type,
          platform: state.contactInfo.platform,
          message: "fake message",
          region: state.contactInfo.region,
          total: 1,
          in_game_username: state.contactInfo.username,
          communication_type: "discord",
          communication_name: state.contactInfo.discord,
          start_time: moment.utc(startTime).format(),
          payment_method: getters.selectedPaymentMethod,
        }
      }
      promises.push(dispatch("bookSession", body))
    }
    await Promise.all(promises)
    commit("setLoading", false)
    commit("setCurrentSession", state.currentSession + 1)
    dispatch("User/Inventory/loadInventory", true, { root: true })
  },
  async checkInBookedSession ({ dispatch }, bookingId) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    await checkInBookedSession(instaProAccessToken, bookingId)
  },
  async bookSession ({ commit, state, dispatch }, payload) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const data = await createSessionBooking(instaProAccessToken, payload)
    if (data === "fail") {
      commit("setFailedSchedules", [...state.failedSchedules, payload])
    }
  },
  async getUpcomingSessions ({ commit, dispatch }) {
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      {
        root: true,
      }
    )
    const data = await getUpcomingSessions(instaProAccessToken)
    if (data.success) {
      commit(
        "setUpcomingSessions",
        data.result.filter(session =>
          [
            BOOKING_STATUS.INITIAL,
            BOOKING_STATUS.PENDING_CHECK_IN,
            BOOKING_STATUS.CHECKED_IN,
          ].includes(session.status)
        )
      )
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/student/coaching/schedule.js